import React, { useEffect, useState } from 'react';
import { departmentsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { Modal, ModalFooter } from './Modals';

const STAGE_LABELS = {
  cleaning: 'Cleaning sign-off',
  sampling: 'Sampling sign-off',
  visual: 'Visual inspection sign-off',
  analysis_chemical: 'Lab analysis — chemical',
  analysis_micro: 'Lab analysis — microbiological',
};

const ManageDepartmentsModal = ({ departments, setDepartments, closeModal }) => {
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [stagePermissions, setStagePermissions] = useState(null);
  const [stageKeys, setStageKeys] = useState([]);
  const [savingStage, setSavingStage] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  useEffect(() => {
    departmentsAPI.getStagePermissions()
      .then((res) => {
        setStagePermissions(res?.data?.data || {});
        setStageKeys(res?.data?.stage_keys || Object.keys(res?.data?.data || {}));
      })
      .catch(() => toast.error('Failed to load sign-off stage permissions.'))
      .finally(() => setPermissionsLoading(false));
  }, []);

  const isDepartmentAllowed = (stageKey, departmentId) =>
    (stagePermissions?.[stageKey] || []).some((d) => String(d.department_id) === String(departmentId));

  const toggleStageDepartment = async (stageKey, departmentId) => {
    const current = stagePermissions?.[stageKey] || [];
    const alreadyAllowed = current.some((d) => String(d.department_id) === String(departmentId));
    const nextIds = alreadyAllowed
      ? current.filter((d) => String(d.department_id) !== String(departmentId)).map((d) => d.department_id)
      : [...current.map((d) => d.department_id), departmentId];
    setSavingStage(stageKey);
    try {
      const res = await departmentsAPI.setStagePermissions(stageKey, nextIds);
      setStagePermissions(res?.data?.data || stagePermissions);
      toast.success('Sign-off permissions updated.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update sign-off permissions.');
    } finally {
      setSavingStage(null);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartmentName.trim()) {
      toast.error('Department name cannot be empty.');
      return;
    }
    try {
      const response = await departmentsAPI.create({ department_name: newDepartmentName });
      setDepartments([...departments, response.data.data]);
      setNewDepartmentName('');
      toast.success('Department added successfully.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add department.');
    }
  };

  const handleUpdateDepartment = async () => {
    if (!editingDepartment || !editingDepartment.department_name.trim()) {
      toast.error('Department name cannot be empty.');
      return;
    }
    try {
      const response = await departmentsAPI.update(editingDepartment.department_id, {
        department_name: editingDepartment.department_name,
        is_active: editingDepartment.is_active,
      });
      setDepartments(
        departments.map((dep) =>
          dep.department_id === editingDepartment.department_id ? response.data.data : dep
        )
      );
      setEditingDepartment(null);
      toast.success('Department updated successfully.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update department.');
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    try {
      await departmentsAPI.delete(departmentId);
      setDepartments(departments.filter((dep) => dep.department_id !== departmentId));
      toast.success('Department deleted successfully.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete department.');
    }
  };

  const activeDepartments = departments.filter((dep) => dep.is_active !== false);

  return (
    <Modal isOpen size="lg" title="Manage Departments" onClose={closeModal}>
        <div className="modal-section">
          <h3 className="modal-section-title">Departments</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Department Name</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((dep) => (
                  <tr key={dep.department_id}>
                    <td>
                      {editingDepartment?.department_id === dep.department_id ? (
                        <input
                          className="form-control"
                          value={editingDepartment.department_name}
                          onChange={(event) =>
                            setEditingDepartment({ ...editingDepartment, department_name: event.target.value })
                          }
                        />
                      ) : (
                        dep.department_name
                      )}
                    </td>
                    <td>
                      <span className={`badge ${dep.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {dep.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="action-cell">
                      {editingDepartment?.department_id === dep.department_id ? (
                        <button className="btn btn-sm btn-primary" onClick={handleUpdateDepartment}>
                          Save
                        </button>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setEditingDepartment({ ...dep })}
                            aria-label={`Edit ${dep.department_name}`}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteDepartment(dep.department_id)}
                            aria-label={`Delete ${dep.department_name}`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-section">
          <h3 className="modal-section-title">Add Department</h3>
            <div className="form-group">
              <label className="form-label" htmlFor="new-department-name">Department Name</label>
              <div style={{ display: 'flex', gap: '8px', maxWidth: '640px' }}>
                <input
                  id="new-department-name"
                  className="form-control"
                  value={newDepartmentName}
                  onChange={(event) => setNewDepartmentName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddDepartment();
                    }
                  }}
                  placeholder="Enter new department name"
                />
                <button type="button" className="btn btn-primary" onClick={handleAddDepartment}>
                  <FaPlus /> Add
                </button>
              </div>
            </div>
        </div>
        <div className="modal-section">
          <h3 className="modal-section-title">Sign-off Stage Permissions</h3>
          <p className="text-muted" style={{ marginTop: '-4px', marginBottom: '12px', fontSize: 'var(--fs-sm)' }}>
            Controls which department a user must belong to in order to sign a given Results stage.
            Checking a box lets any user in that department perform that stage; changes apply immediately.
          </p>
          {permissionsLoading ? (
            <div style={{ padding: '12px', color: 'var(--text-secondary)' }}>Loading…</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Stage</th>
                    {activeDepartments.map((dep) => (
                      <th key={dep.department_id} style={{ textAlign: 'center' }}>{dep.department_name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stageKeys.map((stageKey) => (
                    <tr key={stageKey}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {STAGE_LABELS[stageKey] || stageKey}
                        {savingStage === stageKey && <span style={{ marginLeft: '8px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Saving…</span>}
                      </td>
                      {activeDepartments.map((dep) => (
                        <td key={dep.department_id} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isDepartmentAllowed(stageKey, dep.department_id)}
                            disabled={savingStage === stageKey}
                            onChange={() => toggleStageDepartment(stageKey, dep.department_id)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {stageKeys.length === 0 && (
                    <tr><td colSpan={activeDepartments.length + 1} style={{ textAlign: 'center', padding: '16px', color: 'var(--text-secondary)' }}>No stages configured</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {stageKeys.some((key) => (stagePermissions?.[key] || []).length === 0) && !permissionsLoading && (
            <div style={{ marginTop: '10px', fontSize: 'var(--fs-sm)', padding: '8px 10px', borderRadius: '6px', background: 'var(--warning-bg)', color: 'var(--warning-text)' }}>
              ⚠ At least one stage has no department mapped to it — nobody will be able to sign that step until a department is checked.
            </div>
          )}
        </div>
      <ModalFooter>
        <button type="button" className="btn btn-outline" onClick={closeModal}>Close</button>
      </ModalFooter>
    </Modal>
  );
};

export default ManageDepartmentsModal;
